import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@lightning/ui/collapsible';
import { Textarea } from '@lightning/ui/textarea';
import type { DojoPracticeActivity } from '@/types/dojo';
import { cn } from '@/lib/utils';
import {
  FOLLOW_THROUGH_CHOICES, FOLLOW_THROUGH_NOTE_MAX, isFollowThroughEnabled, isFollowThroughPreview, loadFollowThrough,
  reflectionDay, saveFollowThrough,
  type FollowThroughChoice, type PracticeFollowThrough as Reflection,
} from '@/lib/practiceFollowThrough';

interface Props {
  activity: DojoPracticeActivity[];
  userId?: string;
  preview?: boolean;
}

export function PracticeFollowThrough(props: Props) {
  if (!isFollowThroughPreview(props.preview ?? false) && !isFollowThroughEnabled()) return null;
  return <PracticeFollowThroughContent key={String(props.preview) + (props.userId ?? '')} {...props} />;
}

function PracticeFollowThroughContent({ activity, userId, preview = false }: Props) {
  const [history, setHistory] = useState<Reflection[]>([]);
  const [selectedRound, setSelectedRound] = useState(activity[0]?.round_id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [reload, setReload] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const [saved, setSaved] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const mounted = useRef(true);
  const saveLock = useRef(false);
  const sample = isFollowThroughPreview(preview);
  const practice = activity.find((item) => item.round_id === selectedRound) ?? activity[0];
  const today = reflectionDay();
  const current = history.find((item) => item.round_id === practice?.round_id && item.reflection_day === today);
  const noteKey = (practice?.round_id ?? '') + today;
  const noteDraft = noteDrafts[noteKey] ?? current?.note ?? '';

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    loadFollowThrough(userId, preview, activity).then((rows) => {
      if (active) setHistory(rows);
    }).catch(() => {
      if (active) setLoadError(true);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [userId, preview, activity, reload]);

  async function save(response: FollowThroughChoice, note?: string) {
    if (!practice || saveLock.current || (current?.response === response && note === undefined)) return;
    saveLock.current = true;
    setSaving(true);
    setSaveError(false);
    setSaved(false);
    try {
      const row = await saveFollowThrough(userId, preview, activity, practice, response, note);
      if (!mounted.current) return;
      setHistory((rows) => [row, ...rows.filter((item) =>
        item.round_id !== row.round_id || item.reflection_day !== row.reflection_day)]
        .sort((a, b) => b.reflection_day.localeCompare(a.reflection_day) || b.created_at.localeCompare(a.created_at)));
      setSaved(true);
    } catch {
      if (mounted.current) setSaveError(true);
    } finally {
      saveLock.current = false;
      if (mounted.current) setSaving(false);
    }
  }

  if (!practice) return null;
  return (
    <section aria-labelledby="did-it-travel-title" className="mt-6 rounded-2xl border border-how-space-deep/[0.06] bg-white/70 px-5 py-5 shadow-surface sm:px-6">
      <p className="text-[10px] font-medium uppercase leading-4 tracking-[0.08em] text-how-text-secondary/85">Reflection</p>
      <h2 id="did-it-travel-title" className="mt-1 font-body text-base font-medium leading-5 tracking-[-0.015em] text-how-space-deep">
        Did it travel?
      </h2>
      <p className="mt-1.5 text-[13px] leading-5 text-how-text-secondary/85">
        Did what you practiced in “{practice.title.trim()}” show up in a real situation?
      </p>
      {activity.length > 1 && (
        <label className="mt-3 block text-xs text-how-text-secondary/85">
          Practice
          <span className="relative mt-1 block">
          <select value={practice.round_id} disabled={saving} onChange={(event) => {
            setSelectedRound(event.target.value); setSaved(false); setSaveError(false);
          }} className="block min-h-11 w-full appearance-none rounded-lg border border-how-space-deep/15 bg-white py-2 pl-3 pr-10 text-sm text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/60">
            {activity.map((item) => <option key={item.round_id} value={item.round_id}>
              {item.title} · {new Date(item.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </option>)}
          </select>
          <ChevronDown aria-hidden="true" strokeWidth={1.5} className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-how-text-secondary" />
          </span>
        </label>
      )}
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Reflect on where the practice showed up" aria-busy={saving}>
        {FOLLOW_THROUGH_CHOICES.map((choice) => (
          <button key={choice.value} type="button" aria-pressed={current?.response === choice.value}
            disabled={loading || saving || loadError || (!sample && !userId)}
            onClick={() => void save(choice.value)}
            className={cn(
              'min-h-11 rounded-full border px-3.5 py-2 text-[12px] leading-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/60 focus-visible:ring-offset-2 disabled:opacity-50',
              current?.response === choice.value
                ? 'border-how-space-deep/30 bg-how-rose/45 text-how-space-deep'
                : 'border-how-space-deep/15 bg-white/65 text-how-text-secondary hover:bg-how-rose/35'
            )}>{choice.label}</button>
        ))}
      </div>
      {/* Preview only: see follow-through-handoff.md before enabling private notes. */}
      {sample && current && (
        <div className="mt-4">
          <label htmlFor="follow-through-note" className="text-sm text-how-space-deep">
            What happened? <span className="text-xs text-how-text-secondary/85">(optional)</span>
          </label>
          <Textarea id="follow-through-note" value={noteDraft} maxLength={FOLLOW_THROUGH_NOTE_MAX}
            disabled={saving} rows={3} placeholder="A moment you’d like to remember."
            onChange={(event) => { setNoteDrafts((drafts) => ({ ...drafts, [noteKey]: event.target.value })); setSaved(false); setSaveError(false); }}
            className="mt-2 min-h-24 resize-y rounded-xl border-how-space-deep/15 bg-white/70 font-body text-sm text-how-space-deep"
            aria-describedby="follow-through-note-hint" />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p id="follow-through-note-hint" className="text-xs text-how-text-secondary/85">Optional · up to {FOLLOW_THROUGH_NOTE_MAX} characters</p>
            <button type="button" disabled={saving || noteDraft.trim() === (current.note ?? '')}
              onClick={() => void save(current.response, noteDraft)}
              className="min-h-11 rounded-full border border-how-space-deep/15 bg-white px-4 text-xs text-how-space-deep hover:bg-how-rose/35 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/60">
              Save note
            </button>
          </div>
        </div>
      )}
      <p className="mt-3 text-xs leading-5 text-how-text-secondary/85" role="status">
        {loading ? 'Loading reflections…' : saving ? 'Saving…' : saved
          ? sample ? 'Sample reflection saved for this preview.' : 'Reflection saved.'
          : current ? 'Your response for today. You can change it or return another day.'
          : sample ? 'Sample history · responses stay in this preview tab, not your account.'
          : 'One response per practice each day, saved privately to your account.'}
      </p>
      {loadError && <p role="alert" className="mt-2 text-sm text-how-space-deep">
        We couldn’t load your reflections. <button type="button" className="underline" onClick={() => setReload((value) => value + 1)}>Try again</button>
      </p>}
      {saveError && <p role="alert" className="mt-2 text-sm text-how-space-deep">We couldn’t save your changes. Please try again.</p>}
      {history.length > 0 && (
        <Collapsible className="mt-3">
          <h3>
            <CollapsibleTrigger className="group inline-flex min-h-11 items-center justify-start gap-2 rounded-md text-left font-body text-sm font-medium text-how-space-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/60">
              Reflections over time
              <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 text-how-text-secondary/85 transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none" strokeWidth={1.5} />
            </CollapsibleTrigger>
          </h3>
          <CollapsibleContent>
          <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">
            {sample ? 'Sample responses across several days.' : 'Your recent responses, in your own words.'}
          </p>
          <ol className="mt-3 divide-y divide-how-space-deep/[0.06]">
            {history.slice(0, visibleCount).map((item) => (
              <li key={item.round_id + item.reflection_day} className="py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="text-sm font-medium text-how-space-deep">{FOLLOW_THROUGH_CHOICES.find((choice) => choice.value === item.response)?.label}</p>
                  <time dateTime={item.reflection_day} className="text-xs text-how-text-secondary/85">
                    {new Date(item.reflection_day + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </time>
                </div>
                <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">{item.practice_title}</p>
                {sample && item.note && <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-how-space-deep">{item.note}</p>}
              </li>
            ))}
          </ol>
          {history.length > visibleCount && <button type="button" className="min-h-11 text-xs underline text-how-space-deep"
            onClick={() => setVisibleCount((count) => count + 5)}>Show earlier reflections</button>}
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}
